import React, { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Divider,
    CircularProgress,
    TablePagination,
} from "@mui/material";
import COLORS from "../../../../../constants/colors";

const getStatusColor = (status) => {
    switch (status) {
        case "Open": return "error";
        case "In Progress": return "warning";
        case "Resolved": return "success";
        default: return "default";
    }
};

const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return "th";
        switch (day % 10) {
            case 1: return "st";
            case 2: return "nd";
            case 3: return "rd";
            default: return "th";
        }
    };
    return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
};

const TicketsTable = ({ tickets }) => { // Accept tickets array as a prop
    const [open, setOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const handleOpen = (ticket) => {
        setSelectedTicket(ticket);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedTicket(null);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <TableContainer
                component={Paper}
                sx={{
                    mb: 3,
                    borderRadius: 2,
                    boxShadow: 0,
                    overflowX: "auto",
                    border: `1px solid ${COLORS.border}`,
                }}
            >
                <Table size="medium" sx={{ minWidth: 600 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.9rem", py: 1.5, whiteSpace: 'nowrap' }}>Issue Category</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.9rem", py: 1.5, whiteSpace: 'nowrap' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.9rem", py: 1.5, whiteSpace: 'nowrap' }}>Created At</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.9rem", py: 1.5, whiteSpace: 'nowrap' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tickets.length > 0 ? (
                            tickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((ticket) => (
                                <TableRow
                                    key={ticket.id}
                                    sx={{
                                        "&:last-child td, &:last-child th": { border: 0 },
                                        "&:hover": { backgroundColor: "#f5f5f5" },
                                    }}
                                >
                                    <TableCell sx={{ py: 1.2, fontSize: "0.85rem", whiteSpace: 'nowrap' }}>{ticket.subject}</TableCell>
                                    <TableCell sx={{ py: 1.2, fontSize: "0.85rem", whiteSpace: 'nowrap' }}>
                                        <Chip label={ticket.status} color={getStatusColor(ticket.status)} variant="filled" size="small" />
                                    </TableCell>
                                    <TableCell sx={{ py: 1.2, fontSize: "0.85rem", whiteSpace: 'nowrap' }}>{formatDate(ticket.created_at)}</TableCell>
                                    <TableCell sx={{ py: 1.2, fontSize: "0.85rem", whiteSpace: 'nowrap' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="primary"
                                            sx={{ textTransform: "none", borderRadius: 1.5, px: 1.5, py: 0.5 }}
                                            onClick={() => handleOpen(ticket)}
                                        >
                                            View Details
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 2, fontSize: "0.85rem", color: "#777" }}>
                                    No support tickets found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {tickets.length > 0 && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 15, 25]}
                    component="div"
                    count={tickets.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        fontSize: "0.8rem",
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions": {
                            fontSize: "0.8rem",
                        },
                    }}
                />
            )}

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                sx={{ "& .MuiPaper-root": { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontSize: 22, fontWeight: "bold", textAlign: "left", pb: 1 }}>
                    Ticket Details
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ py: 3, px: 3 }}>
                    {selectedTicket && (
                        <Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    Ticket ID: <span style={{ fontWeight: 'normal' }}>{selectedTicket.id}</span>
                                </Typography>
                                <Chip label={selectedTicket.status} color={getStatusColor(selectedTicket.status)} variant="filled" size="medium" />
                            </Box>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>Issue Category:</strong> {selectedTicket.subject}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>Date Raised:</strong> {formatDate(selectedTicket.created_at)}
                            </Typography>
                            {selectedTicket.status === "Resolved" && selectedTicket.date_resolved && (
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Date Resolved:</strong> {formatDate(selectedTicket.date_resolved)}
                                </Typography>
                            )}
                            <Box sx={{ mt: 3, backgroundColor: "#f9fafb", p: 2.5, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Description:
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#555", lineHeight: 1.6 }}>
                                    {selectedTicket.description}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <Divider />
                <DialogActions sx={{ justifyContent: "flex-end", pb: 2, pr: 2 }}>
                    <Button
                        onClick={handleClose}
                        color="primary"
                        variant="contained"
                        sx={{ textTransform: "none", borderRadius: 2, px: 2.5, py: 1 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TicketsTable;